import { useState } from 'react';
import type { Job } from '@volt/shared';
import { useI18n } from '../i18n/context.js';
import { copyToClipboard } from '../lib/dom.js';
import { downloadUrl } from '../lib/download.js';
import { ContextMenu } from './ContextMenu.js';
import type { MenuItem } from './ContextMenu.js';
import { ConfirmDialog } from './ConfirmDialog.js';
import { Modal } from './Modal.js';
import { Icon } from './Icon.js';
import { ShareMediaModal } from './ShareMediaModal.js';

export interface JobActionsMenuProps {
  job: Job;
  /** URL downloaded by the "download" action (this tile's specific result). `null` hides the item. */
  downloadHref: string | null;
  downloadFilename: string;
  /**
   * Which of the job's results this tile shows. When set (and the job is done),
   * the menu offers "Share" to send the photo/video to a friend or group.
   */
  resultIndex?: number;
  onToggleFavorite: (job: Job, isFavorite: boolean) => void;
  onRegenerate: (job: Job) => void;
  onDelete: (job: Job) => void;
  /** Stops a queued / running generation; offered only while the job is still active. */
  onCancel?: (job: Job) => void;
  className?: string;
}

/**
 * "⋮" trigger button + popover menu for a single job tile: favorite,
 * regenerate, view prompt, download, delete. Reuses `ContextMenu` (viewport-
 * clamped popover), `ConfirmDialog` (delete confirmation) and `Modal` (full
 * prompt viewer with copy) so behaviour/positioning stays consistent with
 * the rest of the app.
 */
export function JobActionsMenu({
  job,
  downloadHref,
  downloadFilename,
  resultIndex,
  onToggleFavorite,
  onRegenerate,
  onDelete,
  onCancel,
  className,
}: JobActionsMenuProps) {
  const { t } = useI18n();
  const [menuPos, setMenuPos] = useState<{ x: number; y: number } | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [promptOpen, setPromptOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const canShare = job.status === 'done' && resultIndex !== undefined && downloadHref !== null;
  const canCancel = Boolean(onCancel) && (job.status === 'pending' || job.status === 'processing');
  const [copied, setCopied] = useState(false);

  const prompt = (job.input as { prompt?: string } | null)?.prompt ?? '';

  const items: MenuItem[] = [
    {
      key: 'favorite',
      label: job.isFavorite ? t('job.favoriteRemove') : t('job.favoriteAdd'),
      icon: 'star',
      onSelect: () => onToggleFavorite(job, !job.isFavorite),
    },
    {
      key: 'regenerate',
      label: t('job.regenerate'),
      icon: 'refresh',
      onSelect: () => onRegenerate(job),
    },
    {
      key: 'viewPrompt',
      label: t('job.viewPrompt'),
      icon: 'search',
      onSelect: () => setPromptOpen(true),
    },
    ...(canShare
      ? [
          {
            key: 'share',
            label: t('job.share'),
            icon: 'share' as const,
            onSelect: () => setShareOpen(true),
          },
        ]
      : []),
    ...(downloadHref
      ? [
          {
            key: 'download',
            label: t('job.download'),
            icon: 'download' as const,
            onSelect: () => downloadUrl(downloadHref, downloadFilename),
          },
        ]
      : []),
    ...(canCancel
      ? [
          {
            key: 'cancel',
            label: t('job.cancel'),
            icon: 'stop' as const,
            onSelect: () => onCancel?.(job),
          },
        ]
      : []),
    {
      key: 'delete',
      label: t('common.delete'),
      icon: 'trash',
      danger: true,
      onSelect: () => setConfirmOpen(true),
    },
  ];

  async function handleCopyPrompt() {
    const ok = await copyToClipboard(prompt);
    if (ok) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    }
  }

  return (
    <>
      <button
        type="button"
        className={`job-tile-menu-btn${className ? ` ${className}` : ''}`}
        aria-label={t('job.actions')}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          const rect = event.currentTarget.getBoundingClientRect();
          setMenuPos({ x: rect.right, y: rect.bottom + 4 });
        }}
      >
        <Icon name="more" size={16} />
      </button>

      <ContextMenu
        open={menuPos !== null}
        x={menuPos?.x ?? 0}
        y={menuPos?.y ?? 0}
        align="right"
        items={items}
        onClose={() => setMenuPos(null)}
      />

      {canShare && (
        <ShareMediaModal
          open={shareOpen}
          job={job}
          resultIndex={resultIndex}
          onClose={() => setShareOpen(false)}
        />
      )}

      <ConfirmDialog
        open={confirmOpen}
        title={t('job.deleteTitle')}
        message={t('job.deleteConfirm')}
        confirmLabel={t('common.delete')}
        onConfirm={() => {
          setConfirmOpen(false);
          onDelete(job);
        }}
        onCancel={() => setConfirmOpen(false)}
      />

      <Modal
        open={promptOpen}
        onClose={() => setPromptOpen(false)}
        title={t('job.promptTitle')}
        closeLabel={t('common.close')}
        footer={
          <button type="button" className="btn btn-ghost" onClick={() => void handleCopyPrompt()}>
            <Icon name={copied ? 'check' : 'copy'} size={16} />
            {copied ? t('message.copied') : t('message.copy')}
          </button>
        }
      >
        <p className="job-prompt-full">{prompt}</p>
      </Modal>
    </>
  );
}
